import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/gateway';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filterListToOptions, IdProps, pushRoute, sequence } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useGatewayForm } from './useGatewayForm';
import { useGatewayRecord } from './useGatewayRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { encodeTabQuery } from '@/services/api/resources/analytics/query';
import { SensorsTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const GatewayEditPage = defineComponent<IdProps>({
  name: 'GatewayEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const entityItems = ref<SelectOptions>([]);

    // create empty record
    const record = useGatewayRecord();

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllEntityItems()),
      ([{ data, version }, entityItemList]) => {
        entityItems.value = filterListToOptions(entityItemList);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => pushRoute({ name: 'sensors', query: encodeTabQuery({ [queryTabName]: SensorsTabNames.GATEWAYS }) }),
    );

    // form
    const { formFields, handleSubmit } = useGatewayForm(record, sendRecord);

    return function render() {
      return container(
        [
          pageTitle(t('gateways.edit')),
          box([loader('form', getRecord, [
            ...formFields(entityItems.value),
            loadingButton(
              t('gateways.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
